import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { useConfigInfos } from '../common';

// Greenhouse v3 (Harvest v3, OAuth authorization-code) offer status filter.
// Mirrors GreenhouseV3OfferStatus in main-v2 functions/greenhouse-v3.
export type GreenhouseV3OfferStatus =
  | 'Accepted'
  | 'Created'
  | 'Rejected'
  | 'Deprecated';

// OAuth (Authorization Code / Greenhouse partner) connect: ask the backend for
// the Greenhouse consent URL and send the admin's browser there. The customer
// never provides any credentials — Dado owns the OAuth app. The backend handles
// the callback (exchanges the code, verifies the credential, stores the tokens
// encrypted) and then redirects back to the /integrations/oauth/greenhouse-v3
// landing route with either `?orgId=<orgId>` (success) or `?error=<reason>`.
export const getGreenhouseV3AuthorizeUrl = async (orgId: string) => {
  const { url } = await callFirebaseFunction<{ url: string }>(
    'greenhouseV3_getAuthorizeUrl',
    { orgId }
  );
  return url;
};

export const disconnectGreenhouseV3 = (orgId: string) => {
  return callFirebaseFunction<{ success: true }>('greenhouseV3_disconnect', {
    orgId
  });
};

// Returns the Greenhouse field list for the import mapping picker
export const getGreenhouseV3Fields = async (orgId: string) => {
  const result = await callFirebaseFunction<{
    fields: { name: string; label: string }[];
  }>('greenhouseV3_getFields', { orgId });
  return result.fields;
};

// Connection status comes from the org's `configInfos.greenhouseV3` projection
// (kept up to date by the integrations-config-manager) — there is no status
// callable. This is a realtime Firestore listener, so the card/source reflect
// (dis)connection without polling. Returns [connected, loading, error].
export const useGreenhouseV3Connected = (
  orgId: string
): [boolean, boolean, Error | undefined] => {
  const [doc, loading, error] = useConfigInfos(orgId);
  const connected =
    doc?.data.configInfos?.[Integration.GREENHOUSE_V3]?.active ?? false;
  return [connected, loading, error];
};
